import Head from 'next/head';
import Link from 'next/link';
import Router from 'next/router';
import getConfig from 'next/config';
import * as gtag from '../utils/gtag.js';
import { ColorThemeBoundary } from './ColorThemeBoundary.jsx';

const { publicRuntimeConfig } = getConfig();

function getTitle(title = null) {
  if (title === null) {
    return 'Penger.no';
  }

  return `${title} - Penger.no`;
}

const recordPageview = url => {
  window.setTimeout(() => {
    gtag.pageview(url);
  });
};

Router.events.on('routeChangeComplete', url => {
  recordPageview(url);
});

function getReferrer() {
  if (document.referrer.indexOf(location.origin) !== -1) {
    return null;
  }

  return document.referrer || null;
}

function detectUserOrigin() {
  const utmTags = gtag.getUtmTags();

  if (utmTags) {
    window.onload = gtag.removeUtmFromUrl;

    return {
      source: 'utm',
      value: utmTags,
    };
  }

  if (getReferrer()) {
    return {
      source: 'referrer',
      value: getReferrer(),
    };
  }

  return null;
}

function storeUserOrigin(origin) {
  if (!window.localStorage) {
    return;
  }

  window.localStorage.setItem('pengerno-user-origin', JSON.stringify(origin));
}

function getCanonicalUrl(href) {
  if (publicRuntimeConfig.isProduction) {
    return `https://penger.no${href}`;
  }

  return href;
}

export function GlobalPage({
  children,
  title = null,
  description = null,
  canonical = null,
  noIndex = false,
}) {
  if (process.browser) {
    const userOrigin = detectUserOrigin();

    if (userOrigin) {
      storeUserOrigin(userOrigin);
    }
  }

  return (
    <ColorThemeBoundary>
      <div className="globalPage">
        <Head>
          <title>{getTitle(title)}</title>
          {description && <meta name="description" content={description} />}
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
          {canonical && (
            <Link href={getCanonicalUrl(canonical)} passHref>
              <link rel="canonical" />
            </Link>
          )}
          {noIndex && <meta name="robots" content="noindex" />}
          <script
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gtag.GA_TRACKING_ID}', { anonymize_ip: true });`,
            }}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','gtmDataLayer','${gtag.GTM_TRACKING_ID}');`,
            }}
          />
        </Head>
        {children}
      </div>
      {publicRuntimeConfig.isProduction && (
        <>
          <script
            src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
            async
          />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtag.GTM_TRACKING_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            ></iframe>
          </noscript>
        </>
      )}
      <style jsx>{`
        .globalPage {
          font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI',
            Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji',
            'Segoe UI Emoji', 'Segoe UI Symbol', 'Segoe UI Symbol';
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.4px;
          background-color: var(--bgBlueish);
        }

        .globalPage :global(a),
        .globalPage :global(button),
        .globalPage :global(input),
        .globalPage :global(select),
        .globalPage :global(textarea),
        .globalPage :global(label) {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </ColorThemeBoundary>
  );
}
